import React, { useContext, useEffect } from 'react'
import { ModalActions } from '../common/modal/Modal'
import { Button, ProgressBar, Text, makeStyles } from '@fluentui/react-components'
import { Grid } from '../common/grid/Grid'
import { GridItem } from '../common/grid/GridItem'
import { t } from '../../i18n/i18n'
import { IListState } from '../../interfaces/IListState'
import { useForm } from 'react-hook-form'
import { FinancialYear, Workplace } from '../../api/schemas/schema'
import { ControlledTextInput } from '../common/inputs/TextInput'
import { ControlledDatePicker } from '../common/inputs/DatePicker'
import { ControlledAccountPicker } from '../common/inputs/ControlledAccountPicker'
import { ControlledMoneyInput } from '../common/inputs/ControlledMoneyInput'
import { ControlledWorkplaceDropdownMulti } from '../common/inputs/ControlledWorkplaceDropdownMulti'
import { ControlledDropDown } from '../common/inputs/ControlledDropdown'
import { enumValuesToOptions } from '../../helpers/enumHelper'
import { DataClassification } from '../../api/schemas/schema'
import { DataContext } from '../../context/DataContext'
import { addFinancialYear } from '../../helpers/riskEconomyHelper'
import { useNavigate } from 'react-router-dom'
import { DraggableModal } from '../common/modal/DraggableModal'
import { Form } from '../common/form/Form'

const useStyles = makeStyles({
   btnRight: { justifyContent: 'end' },
})

type ModalAddFinancialYearProps = {
   setRiskEconomyState: React.Dispatch<React.SetStateAction<IListState<FinancialYear>>>
   riskEconomyState: IListState<FinancialYear>
   workplaces: Workplace[]
}

export const ModalAddFinancialYear = ({ setRiskEconomyState, riskEconomyState, workplaces }: ModalAddFinancialYearProps) => {
   const classes = useStyles()
   const dataContext = useContext(DataContext)
   const { organisation, userAccount } = dataContext.state
   const { open } = riskEconomyState
   const isWorkplaceDisabled = organisation.workplaceLimit === 0
   const defaultWorkplace = workplaces.find((wp) => wp.isDefault)
   const {
      control,
      setValue,
      watch,
      reset,
      handleSubmit,
      formState: { isDirty, isSubmitting, isSubmitSuccessful, isValid },
   } = useForm<FinancialYear>({
      defaultValues: {
         owner: userAccount,
         ownerId: userAccount.id,
         workplaces: defaultWorkplace ? [defaultWorkplace] : [],
         dataClassification: DataClassification.InternalInformation, 
      },
   })
   const navigate = useNavigate()
   const [addedFinancialYear, setAddedFinancialYear] = React.useState<FinancialYear>(null)

   useEffect(() => {
      setValue('workplaces', defaultWorkplace ? [defaultWorkplace] : [])
   }, [defaultWorkplace, isWorkplaceDisabled, setValue, workplaces])

   const onSubmit = async (data: FinancialYear) => {
      const result = await addFinancialYear(setRiskEconomyState, dataContext, data)
      setAddedFinancialYear(result)

      setRiskEconomyState((prev) => ({ ...prev, open: false }))
   }

   const openItem = () => {
      navigate(`/risk-economy/${addedFinancialYear.id}`)
      reset()
      setAddedFinancialYear(null)
   }

   const closeAndReset = () => {
      reset({
         owner: userAccount,
         ownerId: userAccount.id,
         workplaces: defaultWorkplace ? [defaultWorkplace] : [],
         dataClassification: DataClassification.InternalInformation,
      })
      setRiskEconomyState((prev) => ({ ...prev, open: false }))
   }

   return (
      <>
         <DraggableModal title={t('NewFinancialYear')} open={open} onClose={closeAndReset}>
            <Form onSubmit={handleSubmit(onSubmit)}>
               <Grid>
                  <GridItem size="12/12">
                     <ControlledTextInput label={t('FinancialYear')} control={control} name="name" required />
                  </GridItem>
                  <GridItem size="6/12">
                     <ControlledDatePicker label={t('StartDate')} control={control} name="startDate" required />
                  </GridItem>
                  <GridItem size="6/12">
                     <ControlledDatePicker label={t('EndDate')} control={control} name="endDate" required />
                  </GridItem>
                  {/* TODO: Enable workplace selection when multi-workplace support is fully implemented */}
                  {/* <GridItem size="12/12">
                     <ControlledWorkplaceDropdownMulti
                        required
                        control={control}
                        name={'workplaces'}
                        label={t('Workplace')}
                        options={workplaces}
                        disabled={isWorkplaceDisabled}
                        showTooltip={isWorkplaceDisabled}
                        tooltipMsg={t('FeatureRequiresUpgrade')}
                     />
                  </GridItem> */}
                  <GridItem size="6/12">
                     <ControlledAccountPicker label={t('Owner')} control={control} name="owner" watch={watch} setValue={setValue} required />
                  </GridItem>
                  <GridItem size="6/12">
                     <ControlledDropDown
                        required
                        control={control}
                        name={'dataClassification'}
                        label={t('InformationManagement')}
                        options={enumValuesToOptions(DataClassification)}
                     />
                  </GridItem>
                  <GridItem size="6/12">
                     <ControlledMoneyInput label={t('BudgetedRevenue')} control={control} name="budgetedRevenue" />
                  </GridItem>
                  <GridItem size="12/12">
                     <ControlledTextInput label={t('Comment')} control={control} name="comment" multiline />
                  </GridItem>
                  <GridItem size="12/12">
                     <ModalActions>
                        <Button onClick={closeAndReset} disabled={isSubmitting}>
                           {t('Cancel')}
                        </Button>
                        <Button appearance="primary" type="submit" disabled={!isDirty || isSubmitting}>
                           {t('Create')}
                        </Button>
                     </ModalActions>
                  </GridItem>
               </Grid>
            </Form>
         </DraggableModal>

         <DraggableModal
            title={isSubmitting ? t('CreatingFinancialYear') : t('FinancialYearCreated')}
            open={isValid && (isSubmitting || isSubmitSuccessful)}
            noCloseButton
            noDivider
         >
            {isSubmitting ? (
               <>
                  <ProgressBar />
                  <Text>{t('ThisCanTakeAMoment')}</Text>
               </>
            ) : (
               <Text>{t('ClickContinueToGoToFinancialYear')}</Text>
            )}
            <ModalActions className={classes.btnRight}>
               {isSubmitSuccessful ? (
                  <Button appearance="primary" onClick={openItem}>
                     {t('Continue')}
                  </Button>
               ) : null}
            </ModalActions>
         </DraggableModal>
      </>
   )
}
